var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Dashboard widget factories — indicators.
 */
import { registerDashboard } from '../registryCore';
import { clamp, createWidgetGroup, num, setParts, setRefresh, setState, } from '../helpers';
import { addLegend } from '../chartPrimitives';
import { getActiveDashboard } from '../theme';
import { readColorStops, resolveValueColor, } from '../colorStops';
registerDashboard('legend', function (props, app) {
    var group = createWidgetGroup(app, 'legend', props);
    var rawItems = props.items;
    var hasUserItems = props.hasUserLegendItems === true ||
        (!props._chartRebuild && Array.isArray(rawItems) && rawItems.length > 0);
    var items = hasUserItems && (rawItems === null || rawItems === void 0 ? void 0 : rawItems.length)
        ? rawItems
        : [
            { label: 'Series A', color: getActiveDashboard().primary },
            { label: 'Series B', color: getActiveDashboard().secondary },
        ];
    addLegend(app, group, items, 0, 0);
    setState(group, __assign(__assign({}, (hasUserItems ? { items: rawItems } : { items: [] })), { hasUserLegendItems: hasUserItems }));
    return group;
});
registerDashboard('thermometer', function (props, app) {
    var _a;
    var height = num(props, 'height', 120);
    var width = num(props, 'width', 24);
    var value = clamp(num(props, 'value', 50), 0, 100);
    var colorStops = (_a = readColorStops(props)) !== null && _a !== void 0 ? _a : [
        { upTo: 50, color: 'primary' },
        { upTo: 80, color: 'warning' },
        { color: 'danger' },
    ];
    var group = createWidgetGroup(app, 'thermometer', props);
    var tubeH = height - Math.round(width * 1.1);
    var bulbR = Math.max(8, Math.round(width * 0.48));
    var fontSize = Math.max(10, Math.round(width * 0.5));
    var fillColor = resolveValueColor(value, colorStops, getActiveDashboard().primary);
    group.add(app.roundedRect({
        width: width,
        height: tubeH,
        cornerRadius: width / 2,
        fill: getActiveDashboard().thermometerTube,
        stroke: getActiveDashboard().thermometerBorder,
        strokeWidth: 1,
        listening: false,
    }));
    var fillH = (tubeH - 4) * (value / 100);
    var fill = app.roundedRect({
        x: 2,
        y: tubeH - fillH - 2,
        width: width - 4,
        height: fillH,
        cornerRadius: (width - 4) / 2,
        fill: fillColor,
        listening: false,
    });
    var bulb = app.circle({
        x: width / 2 - bulbR,
        y: tubeH - 2,
        radius: bulbR,
        fill: fillColor,
        listening: false,
    });
    group.add(fill, bulb, app.text({
        text: "".concat(Math.round(value), "\u00B0"),
        x: width + 8,
        y: tubeH / 2 - fontSize / 2,
        fontSize: fontSize,
        fontWeight: '600',
        fill: getActiveDashboard().text,
        listening: false,
    }));
    setParts(group, { fill: fill, bulb: bulb });
    setRefresh(group, function (v) {
        var clamped = clamp(v, 0, 100);
        var fh = (tubeH - 4) * (clamped / 100);
        fill.y = tubeH - fh - 2;
        fill.height = fh;
        var next = resolveValueColor(clamped, colorStops, getActiveDashboard().primary);
        fill.fill = next;
        bulb.fill = next;
    });
    setState(group, {
        height: height,
        width: width,
        value: value,
        colorStops: readColorStops(props),
    });
    return group;
});
registerDashboard('compass', function (props, app) {
    var size = num(props, 'size', 100);
    var heading = num(props, 'heading', 0);
    var group = createWidgetGroup(app, 'compass', props, { width: size, height: size });
    var cx = size / 2;
    var r = size / 2 - 4;
    var fontSize = Math.max(8, Math.round(size * 0.1));
    group.add(app.circle({
        x: cx - r,
        y: cx - r,
        radius: r,
        fill: getActiveDashboard().compassFace,
        stroke: getActiveDashboard().compassRing,
        strokeWidth: Math.max(1.5, size / 50),
        shadow: size >= 90 ? { color: 'rgba(0,0,0,0.3)', blur: 6, offsetX: 0, offsetY: 2 } : undefined,
        listening: false,
    }));
    ['N', 'E', 'S', 'W'].forEach(function (label, i) {
        var a = (i / 4) * Math.PI * 2 - Math.PI / 2;
        var lr = r * 0.62;
        group.add(app.text({
            text: label,
            x: cx + lr * Math.cos(a) - fontSize / 2,
            y: cx + lr * Math.sin(a) - fontSize / 2,
            fontSize: fontSize,
            fontWeight: label === 'N' ? '700' : '500',
            fill: label === 'N' ? getActiveDashboard().text : getActiveDashboard().textMuted,
            textAlign: 'center',
            textBaseline: 'middle',
            listening: false,
        }));
    });
    var rad = ((heading - 90) * Math.PI) / 180;
    var needleLen = r * 0.55;
    var needle = app.line({
        x: cx,
        y: cx,
        x2: needleLen * Math.cos(rad),
        y2: needleLen * Math.sin(rad),
        stroke: getActiveDashboard().speedoNeedle,
        strokeWidth: Math.max(2, size / 32),
        lineCap: 'round',
        listening: false,
    });
    group.add(needle, app.circle({
        x: cx - size * 0.05,
        y: cx - size * 0.05,
        radius: size * 0.05,
        fill: getActiveDashboard().compassHub,
        stroke: getActiveDashboard().compassRing,
        strokeWidth: 1,
        listening: false,
    }), app.text({
        text: "".concat(Math.round(heading), "\u00B0"),
        x: cx,
        y: cx + r * 0.22,
        fontSize: Math.max(8, Math.round(size * 0.09)),
        fontWeight: '600',
        fill: getActiveDashboard().text,
        textAlign: 'center',
        textBaseline: 'middle',
        listening: false,
    }));
    setParts(group, { needle: needle });
    setRefresh(group, function (v) {
        var h = ((v - 90) * Math.PI) / 180;
        var len = r * 0.55;
        needle.x2 = len * Math.cos(h);
        needle.y2 = len * Math.sin(h);
    });
    setState(group, { size: size, heading: heading });
    return group;
});
registerDashboard('signalStrength', function (props, app) {
    var level = clamp(num(props, 'value', 3), 0, 5);
    var scale = num(props, 'scale', 1);
    var group = createWidgetGroup(app, 'signalStrength', props);
    var barW = Math.max(5, Math.round(7 * scale));
    var gap = Math.max(2, Math.round(3 * scale));
    var maxH = Math.round(28 * scale);
    var totalW = 5 * barW + 4 * gap;
    var bars = [];
    for (var i = 0; i < 5; i++) {
        var h = Math.round((8 + i * 5) * scale);
        var bar = app.rect({
            x: i * (barW + gap),
            y: maxH - h,
            width: barW,
            height: h,
            fill: i < level ? getActiveDashboard().signalActive : getActiveDashboard().signalInactive,
            cornerRadius: Math.max(1, scale),
            listening: false,
        });
        bars.push(bar);
        group.add(bar);
    }
    setParts(group, { bars: bars });
    setRefresh(group, function (v) {
        var lv = clamp(Math.round(v), 0, 5);
        bars.forEach(function (bar, i) {
            bar.fill = i < lv ? getActiveDashboard().signalActive : getActiveDashboard().signalInactive;
        });
    });
    setState(group, { value: level, scale: scale, width: totalW, height: maxH });
    return group;
});
